import { motion } from "framer-motion";

/**
 * Squiggle — signature LevelLeven motif.
 * Animates the pathLength on enter. Handdrawn feel via variable curves.
 */
export function Squiggle({
  color = "#E06B3F",
  width = 400,
  height = 40,
  strokeWidth = 5,
  delay = 0,
  duration = 1.6,
  d,
  className = "",
  once = true,
}) {
  const path =
    d ||
    "M 5 25 Q 40 -5, 80 25 T 160 25 T 240 25 T 320 25 T 395 25";
  return (
    <svg
      className={className}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <motion.path
        d={path}
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        initial={{ pathLength: 0, opacity: 0 }}
        whileInView={{ pathLength: 1, opacity: 1 }}
        viewport={{ once, margin: "-8%" }}
        transition={{ duration, delay, ease: [0.22, 1, 0.36, 1] }}
      />
    </svg>
  );
}

/**
 * SquiggleUnderline — inline version to underline a word.
 */
export function SquiggleUnderline({
  color = "#E06B3F",
  className = "",
  offset = 4,
  height = 14,
  delay = 0.2,
}) {
  return (
    <span
      className={`absolute left-0 right-0 pointer-events-none ${className}`}
      style={{ bottom: -offset, height }}
      aria-hidden
    >
      <svg
        viewBox="0 0 400 20"
        preserveAspectRatio="none"
        className="w-full h-full"
      >
        <motion.path
          d="M 4 12 Q 60 -4, 120 12 T 240 12 T 396 12"
          stroke={color}
          strokeWidth="5"
          strokeLinecap="round"
          fill="none"
          initial={{ pathLength: 0 }}
          whileInView={{ pathLength: 1 }}
          viewport={{ once: true, margin: "-5%" }}
          transition={{ duration: 1.2, delay, ease: [0.22, 1, 0.36, 1] }}
        />
      </svg>
    </span>
  );
}

/**
 * RouteLine — long path for section separators, subway-map feel.
 */
export function RouteLine({
  color = "#97B79D",
  accent = "#E06B3F",
  className = "",
  stops = ["Startpunt", "Vonk", "Speelruimte", "Volgende level"],
}) {
  return (
    <div className={className}>
      <svg viewBox="0 0 1200 80" preserveAspectRatio="none" fill="none" className="w-full h-16">
        <motion.path
          d="M 10 40 C 200 10, 380 70, 580 40 S 940 10, 1190 50"
          stroke={color}
          strokeWidth="3"
          strokeLinecap="round"
          initial={{ pathLength: 0 }}
          whileInView={{ pathLength: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 2.2, ease: [0.22, 1, 0.36, 1] }}
        />
        {stops.map((_, i) => {
          const x = 10 + i * ((1180) / (stops.length - 1));
          const y = i === 0 ? 40 : i === 1 ? 30 : i === 2 ? 50 : 50;
          return (
            <motion.circle
              key={i}
              cx={x}
              cy={y}
              r="6"
              fill="#FAF7F1"
              stroke={i === stops.length - 1 ? accent : color}
              strokeWidth="3"
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 + i * 0.25 }}
            />
          );
        })}
      </svg>
      <div className="mt-2 grid" style={{ gridTemplateColumns: `repeat(${stops.length}, 1fr)` }}>
        {stops.map((s, i) => (
          <span
            key={s}
            className="text-[10px] uppercase tracking-[0.28em] text-[#1F5132]/70"
            style={{ textAlign: i === 0 ? "left" : i === stops.length - 1 ? "right" : "center" }}
          >
            {s}
          </span>
        ))}
      </div>
    </div>
  );
}

/**
 * FloatingBlob — soft ambient shape for hero backgrounds.
 */
export function FloatingBlob({ color, size = 200, className = "", delay = 0 }) {
  return (
    <motion.div
      aria-hidden
      className={`absolute rounded-full pointer-events-none ${className}`}
      style={{ width: size, height: size, background: color }}
      animate={{
        y: [0, -22, 0],
        x: [0, 14, 0],
        rotate: [0, 6, 0],
      }}
      transition={{
        duration: 9 + delay,
        repeat: Infinity,
        ease: "easeInOut",
        delay,
      }}
    />
  );
}
